import React, { useState, useEffect } from 'react';
import { ActionConfigPopup } from '../../../actions';
import * as Icons from 'lucide-react';
import { useAppStore } from '../../../../store/useAppStore';
import { fetchAppPagesWithVersions } from '../../../../services/firestoreService';

export const FooterForm = ({ component, onUpdate, layoutId }) => {
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [editingLinkIndex, setEditingLinkIndex] = useState(null);
  const [editingColumnIndex, setEditingColumnIndex] = useState(null);
  const [pages, setPages] = useState([]);
  const { appName } = useAppStore();

  // Load pages for the app
  useEffect(() => {
    const loadPages = async () => {
      if (!appName) return;
      try {
        const fetchedPages = await fetchAppPagesWithVersions(appName);
        setPages(fetchedPages);
      } catch (error) {
        console.error('Failed to load pages:', error);
      }
    };
    loadPages();
  }, [appName]);

  const handleChange = (field, value) => {
    onUpdate(component.id, {
      specificAttrs: { ...component.specificAttrs, [field]: value }
    });
  };

  const { specificAttrs } = component;
  const {
    footerStyle = 'multi-column',
    scope = 'all-pages',
    columns = [],
    copyrightText = '© 2024 Your Company. All rights reserved.',
    showSocialLinks = true,
    socialLinks = [],
    backgroundColor = '#1F2937',
    textColor = '#D1D5DB',
    linkColor = '#FFFFFF',
    linkHoverColor = '#3B82F6'
  } = specificAttrs;

  // Add new column
  const handleAddColumn = () => {
    const newColumn = {
      id: `column_${Date.now()}`,
      title: 'New Column',
      links: []
    };
    handleChange('columns', [...columns, newColumn]);
  };

  // Remove column
  const handleRemoveColumn = (index) => {
    const updatedColumns = columns.filter((_, i) => i !== index);
    handleChange('columns', updatedColumns);
  };

  // Update column field
  const handleColumnChange = (index, field, value) => {
    const updatedColumns = [...columns];
    updatedColumns[index] = { ...updatedColumns[index], [field]: value };
    handleChange('columns', updatedColumns);
  };

  // Add link to column
  const handleAddLink = (columnIndex) => {
    const updatedColumns = [...columns];
    const newLink = {
      id: `link_${Date.now()}`,
      label: 'New Link',
      targetPage: '',
      externalUrl: '',
      openInNewTab: false,
      action: null
    };
    updatedColumns[columnIndex].links = [...(updatedColumns[columnIndex].links || []), newLink];
    handleChange('columns', updatedColumns);
  };

  // Remove link from column
  const handleRemoveLink = (columnIndex, linkIndex) => {
    const updatedColumns = [...columns];
    updatedColumns[columnIndex].links = updatedColumns[columnIndex].links.filter((_, i) => i !== linkIndex);
    handleChange('columns', updatedColumns);
  };

  // Update link field
  const handleLinkChange = (columnIndex, linkIndex, field, value) => {
    const updatedColumns = [...columns];
    updatedColumns[columnIndex].links[linkIndex] = {
      ...updatedColumns[columnIndex].links[linkIndex],
      [field]: value
    };
    handleChange('columns', updatedColumns);
  };

  // Open action configuration popup for link
  const handleConfigureAction = (columnIndex, linkIndex) => {
    setEditingColumnIndex(columnIndex);
    setEditingLinkIndex(linkIndex);
    setShowActionPopup(true);
  };

  // Save action to link
  const handleActionSelect = (actionConfig) => {
    if (editingColumnIndex !== null && editingLinkIndex !== null) {
      handleLinkChange(editingColumnIndex, editingLinkIndex, 'action', actionConfig);
    }
    setShowActionPopup(false);
    setEditingColumnIndex(null);
    setEditingLinkIndex(null);
  };

  // Social link handlers
  const handleAddSocialLink = () => {
    const newSocial = {
      id: `social_${Date.now()}`,
      platform: 'facebook',
      url: '',
      icon: 'Facebook'
    };
    handleChange('socialLinks', [...socialLinks, newSocial]);
  };

  const handleRemoveSocialLink = (index) => {
    const updated = socialLinks.filter((_, i) => i !== index);
    handleChange('socialLinks', updated);
  };

  const handleSocialLinkChange = (index, field, value) => {
    const updated = [...socialLinks];
    updated[index] = { ...updated[index], [field]: value };
    handleChange('socialLinks', updated);
  };

  // Available social platforms
  const socialPlatforms = [
    { value: 'facebook', label: 'Facebook', icon: 'Facebook' },
    { value: 'twitter', label: 'Twitter/X', icon: 'Twitter' },
    { value: 'instagram', label: 'Instagram', icon: 'Instagram' },
    { value: 'linkedin', label: 'LinkedIn', icon: 'Linkedin' },
    { value: 'youtube', label: 'YouTube', icon: 'Youtube' },
    { value: 'github', label: 'GitHub', icon: 'Github' }
  ];

  return (
    <div style={{ display: 'grid', gap: '16px', padding: '4px' }}>
      {/* Section 1: Basic Settings */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Basic Settings
        </h4>

        {/* Footer Style */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Footer Style
          </label>
          <select
            value={footerStyle}
            onChange={(e) => handleChange('footerStyle', e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              backgroundColor: '#FFFFFF'
            }}
          >
            <option value="multi-column">Multi-Column Links</option>
            <option value="simple-centered">Simple Centered</option>
            <option value="minimal">Minimal (Copyright Only)</option>
            <option value="newsletter">Newsletter Signup</option>
          </select>
        </div>

        {/* Scope */}
        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '6px', 
            color: '#4B5563' 
          }}>
            Apply To
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              fontSize: '13px',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="scope"
                value="this-page-only"
                checked={scope === 'this-page-only'}
                onChange={(e) => handleChange('scope', e.target.value)}
              />
              This Page Only
            </label>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              fontSize: '13px',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="scope"
                value="all-pages"
                checked={scope === 'all-pages'}
                onChange={(e) => handleChange('scope', e.target.value)}
              />
              All Pages
            </label>
          </div>
        </div>
      </div>

      {/* Section 2: Footer Columns */}
      {footerStyle === 'multi-column' && (
        <div style={{ 
          padding: '12px', 
          backgroundColor: '#F9FAFB', 
          borderRadius: '8px',
          border: '1px solid #E5E7EB'
        }}>
          <h4 style={{ 
            margin: '0 0 12px', 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#374151' 
          }}>
            Footer Columns
          </h4>

          {columns.length === 0 && (
            <p style={{ 
              fontSize: '12px', 
              color: '#6B7280', 
              fontStyle: 'italic',
              marginBottom: '12px'
            }}>
              No columns yet. Click "Add Column" to get started.
            </p>
          )}

          {columns.map((column, columnIndex) => (
            <div 
              key={column.id}
              style={{ 
                padding: '12px',
                backgroundColor: '#FFFFFF',
                borderRadius: '6px',
                border: '1px solid #E5E7EB',
                marginBottom: '8px'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#374151' 
                }}>
                  Column {columnIndex + 1}
                </span>
                <button
                  onClick={() => handleRemoveColumn(columnIndex)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '11px',
                    color: '#DC2626',
                    backgroundColor: '#FEE2E2',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Remove ✕
                </button>
              </div>

              {/* Column Title */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '11px', 
                  fontWeight: '500', 
                  marginBottom: '3px', 
                  color: '#6B7280' 
                }}>
                  Column Title
                </label>
                <input
                  type="text"
                  value={column.title}
                  onChange={(e) => handleColumnChange(columnIndex, 'title', e.target.value)}
                  placeholder="e.g., Company, Resources, Support"
                  style={{
                    width: '100%',
                    padding: '5px 8px',
                    fontSize: '12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px'
                  }}
                />
              </div>

              {/* Links in this column */}
              <div style={{ 
                marginLeft: '12px',
                paddingLeft: '12px',
                borderLeft: '2px solid #E5E7EB'
              }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '11px', 
                  fontWeight: '500', 
                  marginBottom: '6px', 
                  color: '#6B7280' 
                }}>
                  Links
                </label>

                {(!column.links || column.links.length === 0) && (
                  <p style={{ 
                    fontSize: '10px', 
                    color: '#9CA3AF', 
                    fontStyle: 'italic',
                    marginBottom: '8px'
                  }}>
                    No links in this column yet.
                  </p>
                )}

                {column.links && column.links.map((link, linkIndex) => (
                  <div 
                    key={link.id}
                    style={{ 
                      padding: '8px',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '4px',
                      marginBottom: '6px',
                      border: '1px solid #E5E7EB'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '6px'
                    }}>
                      <span style={{ fontSize: '10px', color: '#6B7280' }}>
                        Link {linkIndex + 1}
                      </span>
                      <button
                        onClick={() => handleRemoveLink(columnIndex, linkIndex)}
                        style={{
                          padding: '2px 6px',
                          fontSize: '10px',
                          color: '#DC2626',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #FCA5A5',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Link Label */}
                    <div style={{ marginBottom: '6px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '10px', 
                        marginBottom: '2px', 
                        color: '#6B7280' 
                      }}>
                        Label
                      </label>
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => handleLinkChange(columnIndex, linkIndex, 'label', e.target.value)}
                        placeholder="Link text"
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          fontSize: '11px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '3px'
                        }}
                      />
                    </div>

                    {/* Target Page */}
                    <div style={{ marginBottom: '6px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '10px', 
                        marginBottom: '2px', 
                        color: '#6B7280' 
                      }}>
                        Target Page
                      </label>
                      <select
                        value={link.targetPage || ''}
                        onChange={(e) => handleLinkChange(columnIndex, linkIndex, 'targetPage', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          fontSize: '11px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '3px',
                          backgroundColor: '#FFFFFF'
                        }}
                      >
                        <option value="">-- Select Page --</option>
                        {pages.map(page => (
                          <option key={page.id || page.name} value={page.pageName || page.name || page.id}>
                            {page.pageName || page.name || page.id}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* External URL */}
                    <div style={{ marginBottom: '6px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '10px', 
                        marginBottom: '2px', 
                        color: '#6B7280' 
                      }}>
                        Or External URL
                      </label>
                      <input
                        type="text"
                        value={link.externalUrl || ''}
                        onChange={(e) => handleLinkChange(columnIndex, linkIndex, 'externalUrl', e.target.value)}
                        placeholder="https://example.com"
                        style={{
                          width: '100%',
                          padding: '4px 6px',
                          fontSize: '11px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '3px'
                        }}
                      />
                    </div>

                    {/* Open in New Tab */}
                    <div style={{ marginBottom: '6px' }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px',
                        fontSize: '10px',
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={link.openInNewTab || false}
                          onChange={(e) => handleLinkChange(columnIndex, linkIndex, 'openInNewTab', e.target.checked)}
                        />
                        <span style={{ color: '#6B7280' }}>Open in New Tab</span>
                      </label>
                    </div>

                    {/* Action */}
                    <div>
                      <button
                        onClick={() => handleConfigureAction(columnIndex, linkIndex)}
                        style={{
                          width: '100%',
                          padding: '4px 8px',
                          fontSize: '10px',
                          color: '#FFFFFF',
                          backgroundColor: link.action ? '#10B981' : '#3B82F6',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        {link.action ? '✓ Action Set' : 'Set Action'}
                      </button>
                    </div>
                  </div>
                ))}

                {/* Add Link Button */}
                <button
                  onClick={() => handleAddLink(columnIndex)}
                  style={{
                    width: '100%',
                    padding: '5px 8px',
                    fontSize: '11px',
                    color: '#3B82F6',
                    backgroundColor: '#FFFFFF',
                    border: '1px dashed #3B82F6',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  + Add Link
                </button>
              </div>
            </div>
          ))}

          {/* Add Column Button */}
          <button
            onClick={handleAddColumn}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '13px',
              color: '#3B82F6',
              backgroundColor: '#EFF6FF',
              border: '1px dashed #3B82F6',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            + Add Column
          </button>
        </div>
      )}

      {/* Section 3: Social Links */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Social Media Links
        </h4>

        {/* Show Social Links Toggle */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '12px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={showSocialLinks}
              onChange={(e) => handleChange('showSocialLinks', e.target.checked)}
            />
            <span style={{ color: '#4B5563', fontWeight: '500' }}>Show Social Links</span>
          </label>
        </div>

        {showSocialLinks && (
          <>
            {socialLinks.length === 0 && (
              <p style={{ 
                fontSize: '12px', 
                color: '#6B7280', 
                fontStyle: 'italic',
                marginBottom: '12px'
              }}>
                No social links yet. Click "Add Social Link" to get started.
              </p>
            )}

            {socialLinks.map((social, index) => (
              <div 
                key={social.id}
                style={{ 
                  padding: '10px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB',
                  marginBottom: '8px'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: '#374151' 
                  }}>
                    Social Link {index + 1}
                  </span>
                  <button
                    onClick={() => handleRemoveSocialLink(index)}
                    style={{
                      padding: '3px 7px',
                      fontSize: '10px',
                      color: '#DC2626',
                      backgroundColor: '#FEE2E2',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove ✕
                  </button>
                </div>

                {/* Platform */}
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '11px', 
                    fontWeight: '500', 
                    marginBottom: '3px', 
                    color: '#6B7280' 
                  }}>
                    Platform
                  </label>
                  <select
                    value={social.platform}
                    onChange={(e) => {
                      const platform = socialPlatforms.find(p => p.value === e.target.value);
                      handleSocialLinkChange(index, 'platform', e.target.value);
                      handleSocialLinkChange(index, 'icon', platform?.icon || 'Circle');
                    }}
                    style={{
                      width: '100%',
                      padding: '5px 8px',
                      fontSize: '12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF'
                    }}
                  >
                    {socialPlatforms.map(platform => (
                      <option key={platform.value} value={platform.value}>
                        {platform.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* URL */}
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '11px', 
                    fontWeight: '500', 
                    marginBottom: '3px', 
                    color: '#6B7280' 
                  }}>
                    Profile URL
                  </label>
                  <input
                    type="text"
                    value={social.url || ''}
                    onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                    placeholder="https://facebook.com/yourpage"
                    style={{
                      width: '100%',
                      padding: '5px 8px',
                      fontSize: '12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>
            ))}

            {/* Add Social Link Button */}
            <button
              onClick={handleAddSocialLink}
              style={{
                width: '100%',
                padding: '7px 10px',
                fontSize: '12px',
                color: '#3B82F6',
                backgroundColor: '#EFF6FF',
                border: '1px dashed #3B82F6',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              + Add Social Link
            </button>
          </>
        )}
      </div>

      {/* Section 4: Copyright Text */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Copyright Text
        </h4>

        <div>
          <label style={{ 
            display: 'block', 
            fontSize: '12px', 
            fontWeight: '500', 
            marginBottom: '4px', 
            color: '#4B5563' 
          }}>
            Copyright Notice
          </label>
          <input
            type="text"
            value={copyrightText}
            onChange={(e) => handleChange('copyrightText', e.target.value)}
            placeholder="© 2024 Your Company. All rights reserved."
            style={{
              width: '100%',
              padding: '6px 10px',
              fontSize: '13px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px'
            }}
          />
        </div>
      </div>

      {/* Section 5: Styling */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#F9FAFB', 
        borderRadius: '8px',
        border: '1px solid #E5E7EB'
      }}>
        <h4 style={{ 
          margin: '0 0 12px', 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Colors & Style
        </h4>

        <div style={{ display: 'grid', gap: '10px' }}>
          {/* Background Color */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Background Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={backgroundColor}
                onChange={(e) => handleChange('backgroundColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>

          {/* Text Color */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Text Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={textColor}
                onChange={(e) => handleChange('textColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={textColor}
                onChange={(e) => handleChange('textColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>

          {/* Link Color */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Link Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={linkColor}
                onChange={(e) => handleChange('linkColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={linkColor}
                onChange={(e) => handleChange('linkColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>

          {/* Link Hover Color */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '12px', 
              fontWeight: '500', 
              marginBottom: '4px', 
              color: '#4B5563' 
            }}>
              Link Hover Color
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={linkHoverColor}
                onChange={(e) => handleChange('linkHoverColor', e.target.value)}
                style={{
                  width: '50px',
                  height: '34px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={linkHoverColor}
                onChange={(e) => handleChange('linkHoverColor', e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Configuration Popup */}
      {showActionPopup && (
        <ActionConfigPopup
          isOpen={showActionPopup}
          onClose={() => {
            setShowActionPopup(false);
            setEditingColumnIndex(null);
            setEditingLinkIndex(null);
          }}
          onActionSelect={handleActionSelect}
        />
      )}
    </div>
  );
};